import { AllowedIcons } from '@thyme/nashville/src/types/icons'
import { useRouter } from 'vue-router'
import { CommunicationType } from '@/legacy/types/communications/communications'
import { CitedContextDatapoint, LlmSourceType } from '@/libs/features/llm/types'
import { formatQuotes, formatSourceHeader } from './format'
import { LinkDestination } from './types'

/**
 *
 *
 */
export default function () {
  const router = useRouter()

  /**
   * Dest contains an optional path and optional query param to navigate to.
   * If either is missing, keep the existing value from the current route.
   * Then, navigate to the resulting route.
   * @param dest
   */
  function navigateToTab(dest?: LinkDestination) {
    if (!dest) return

    // keep existing query params if no new ones provided
    if (!dest.query) {
      dest.query = router.currentRoute.value?.query
    }

    // keep existing path if no new path provided
    if (!dest.path) {
      dest.path = router.currentRoute.value?.path
    }

    void router.push(dest)
  }

  /**
   * Given a sourceType and optional sourceSubtype, return the name of the icon to use.
   * @param sourceType
   * @param sourceSubtype
   */
  function getIconName(
    sourceType: LlmSourceType,
    sourceSubtype?: string
  ): string {
    const communicationIcons: Record<
      CommunicationType,
      keyof typeof AllowedIcons
    > = {
      CALL: 'phone',
      SMS: 'textMessage',
      EMAIL: 'email',
      FAX: 'fax',
      OTHER: 'chatSolid',
    }

    switch (sourceType) {
      case LlmSourceType.COMMUNICATION: {
        return communicationIcons[
          (sourceSubtype?.toUpperCase() as CommunicationType) || 'OTHER'
        ]
      }
      case LlmSourceType.CLAIM: {
        return 'documentText'
      }
      case LlmSourceType.SUBTASK: {
        return 'taskName'
      }
      case LlmSourceType.GOAL: {
        return 'checkCircle'
      }
      case LlmSourceType.CARE_PLAN: {
        return 'pathway'
      }
      case LlmSourceType.PROFILE: {
        return 'userCircle'
      }
      default: {
        return 'chatSolid'
      }
    }
  }

  const patientProfileTabSlugs = new Set([
    'care-plan',
    'clinical-summary',
    'social-summary',
    'history',
    'to-do',
    'symptoms',
    'texting',
    'medical-context',
    'documents',
  ])

  /**
   * Given a sourceType or sourceSubtype, return the corresponding patient profile tab slug, or null if none.
   * This is used for linking to the appropriate tab from a source subtype.
   * @param sourceType
   */
  function toTabSlug(sourceType?: string): string | null {
    if (!sourceType) return null
    const slug = sourceType.toLowerCase().replaceAll('_', '-')
    return patientProfileTabSlugs.has(slug) ? slug : null
  }

  /**
   * Given a source, return the link destination if applicable, or null otherwise.
   * @param source
   * @param patientId
   */
  function linkDestinationForSource(
    source: CitedContextDatapoint,
    patientId: string
  ): LinkDestination {
    const basePatientPath = `/patient/${patientId}`
    switch (source.sourceType) {
      case LlmSourceType.COMMUNICATION: {
        return {
          query: { commId: source.sourceId },
        }
      }
      case LlmSourceType.SUBTASK: {
        return {
          query: { subtaskId: source.sourceId },
        }
      }
      default: {
        const tabSlug =
          toTabSlug(source.sourceType) ?? toTabSlug(source.sourceSubtype)
        if (!tabSlug) return null

        return {
          path: `${basePatientPath}/${tabSlug}`,
        }
      }
    }
  }

  /**
   * Returns a value indicating whether the source can have a link destination.
   * @param source
   * @param patientId
   */
  function isLinkApplicable(
    source: CitedContextDatapoint,
    patientId: string
  ): boolean {
    return !!linkDestinationForSource(source, patientId)
  }

  return {
    getIconName,
    formatSourceHeader,
    isLinkApplicable,
    linkDestinationForSource,
    navigateToTab,
    formatQuotes,
  }
}
